import type { SpreadsheetSheetProtectionOptions } from '../protocol/spreadsheetEngineProtocol'

export type SheetProtectionOptionKey =
  | 'allow_select_locked_cells'
  | 'allow_select_unlocked_cells'
  | 'allow_format_cells'
  | 'allow_resize_rows'
  | 'allow_resize_columns'
  | 'allow_insert_rows'
  | 'allow_insert_columns'
  | 'allow_delete_rows'
  | 'allow_delete_columns'

export type SheetProtectionOptionPresentation = {
  key: SheetProtectionOptionKey
  label: string
  title: string
  checked: boolean
}

export type SheetProtectionDialogPresentation = {
  ariaLabel: string
  title: string
  closeAriaLabel: string
  closeTitle: string
  sheetLabel: string
  sheetTitle: string
  primaryLabel: string
  primaryTitle: string
  optionsLegend: string
  options: SheetProtectionOptionPresentation[]
  unprotectTitle: string
  cancelTitle: string
  applyTitle: string
}

export type SheetProtectionApplyPlan = {
  protection: SpreadsheetSheetProtectionOptions | null
  options: {
    sheetId: number | null
  }
}

export type SheetProtectionResizeAxis = 'row' | 'column'

export type SheetProtectionResizePermission = {
  blocked: boolean
  reason: string
  title: string
  cursor: 'row-resize' | 'col-resize' | 'not-allowed'
}

export const DEFAULT_SHEET_PROTECTION_OPTIONS: SpreadsheetSheetProtectionOptions = {
  enabled: true,
  allow_select_locked_cells: true,
  allow_select_unlocked_cells: true,
  allow_format_cells: false,
  allow_resize_rows: false,
  allow_resize_columns: false,
  allow_insert_rows: false,
  allow_insert_columns: false,
  allow_delete_rows: false,
  allow_delete_columns: false,
}

const SHEET_PROTECTION_OPTION_DEFINITIONS: Array<{
  key: SheetProtectionOptionKey
  label: string
  title: string
}> = [
  {
    key: 'allow_select_locked_cells',
    label: 'Select locked cells',
    title: 'Allow users to select locked cells while the sheet is protected',
  },
  {
    key: 'allow_select_unlocked_cells',
    label: 'Select unlocked cells',
    title: 'Allow users to select unlocked cells while the sheet is protected',
  },
  {
    key: 'allow_format_cells',
    label: 'Format cells',
    title: 'Allow users to change cell formatting while the sheet is protected',
  },
  {
    key: 'allow_resize_rows',
    label: 'Resize rows',
    title: 'Allow users to change row height while the sheet is protected',
  },
  {
    key: 'allow_resize_columns',
    label: 'Resize columns',
    title: 'Allow users to change column width while the sheet is protected',
  },
  {
    key: 'allow_insert_rows',
    label: 'Insert rows',
    title: 'Allow users to insert rows while the sheet is protected',
  },
  {
    key: 'allow_insert_columns',
    label: 'Insert columns',
    title: 'Allow users to insert columns while the sheet is protected',
  },
  {
    key: 'allow_delete_rows',
    label: 'Delete rows',
    title: 'Allow users to delete rows while the sheet is protected',
  },
  {
    key: 'allow_delete_columns',
    label: 'Delete columns',
    title: 'Allow users to delete columns while the sheet is protected',
  },
]

export function sheetProtectionIsEnabled(
  protection?: SpreadsheetSheetProtectionOptions | null,
): boolean {
  if (!protection) return false
  if (Object.prototype.hasOwnProperty.call(protection, 'enabled')) {
    return protection.enabled !== false
  }
  return true
}

export function normalizeSheetProtectionDraftOptions(
  protection?: SpreadsheetSheetProtectionOptions | null,
): SpreadsheetSheetProtectionOptions {
  return {
    ...DEFAULT_SHEET_PROTECTION_OPTIONS,
    ...(protection ?? {}),
    enabled: true,
  }
}

export function buildSheetProtectionApplyPayload(args: {
  enabled: boolean
  draftOptions: SpreadsheetSheetProtectionOptions
}): SpreadsheetSheetProtectionOptions | null {
  if (!args.enabled) return null
  return {
    ...normalizeSheetProtectionDraftOptions(args.draftOptions),
    enabled: true,
  }
}

export function buildSheetProtectionApplyPlan(args: {
  protection?: SpreadsheetSheetProtectionOptions | null
  activeSheetId?: number | null
}): SheetProtectionApplyPlan {
  return {
    protection: args.protection ? normalizeSheetProtectionDraftOptions(args.protection) : null,
    options: {
      sheetId: args.activeSheetId ?? null,
    },
  }
}

export function buildSheetProtectionResizePermission(args: {
  axis: SheetProtectionResizeAxis
  protection?: SpreadsheetSheetProtectionOptions | null
}): SheetProtectionResizePermission {
  const protectedSheet = sheetProtectionIsEnabled(args.protection)
  const allowed = args.axis === 'column'
    ? args.protection?.allow_resize_columns === true
    : args.protection?.allow_resize_rows === true
  const blocked = protectedSheet && !allowed
  if (args.axis === 'column') {
    const reason = 'Protected sheet does not allow resizing columns.'
    return {
      blocked,
      reason,
      title: blocked ? reason : 'Resize column width',
      cursor: blocked ? 'not-allowed' : 'col-resize',
    }
  }
  const reason = 'Protected sheet does not allow resizing rows.'
  return {
    blocked,
    reason,
    title: blocked ? reason : 'Resize row height',
    cursor: blocked ? 'not-allowed' : 'row-resize',
  }
}

export function buildSheetProtectionDialogPresentation(args: {
  sheetName?: string | null
  draftOptions: SpreadsheetSheetProtectionOptions
}): SheetProtectionDialogPresentation {
  const sheetLabel = String(args.sheetName || '').trim() || 'current sheet'
  return {
    ariaLabel: 'Protect Sheet',
    title: 'Protect Sheet',
    closeAriaLabel: 'Close Protect Sheet',
    closeTitle: 'Close Protect Sheet',
    sheetLabel,
    sheetTitle: `Protect ${sheetLabel}`,
    primaryLabel: 'Protect sheet contents and locked cells',
    primaryTitle: 'Protect sheet contents and locked cells',
    optionsLegend: 'Allow all users of this sheet to:',
    options: SHEET_PROTECTION_OPTION_DEFINITIONS.map((option) => ({
      ...option,
      checked: args.draftOptions[option.key] === true,
    })),
    unprotectTitle: 'Remove sheet protection',
    cancelTitle: 'Cancel sheet protection changes',
    applyTitle: 'Apply sheet protection options',
  }
}
